import { Badge } from "@/components/ui/badge";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssets } from "@/hooks/use-assets";
import {
  ASSET_CATEGORIES,
  type AssetCategory,
  assetCategoryPath,
  countUniqueAssetsByTopLevel,
  getGroupLabel,
} from "@/lib/asset-utils";
import { cn } from "@/lib/utils";
import { Blocks, Bot, Container, type LucideIcon, Wrench } from "lucide-react";
import { Link } from "react-router";

const CATEGORY_DETAILS: Record<AssetCategory["key"], { icon: LucideIcon; description: string }> = {
  "docker & helm": {
    icon: Container,
    description: "Helm charts and Docker images for deploying the Istari Platform.",
  },
  agent: {
    icon: Bot,
    description: "Istari Agent installers for connecting your compute environment to the platform.",
  },
  modules: {
    icon: Blocks,
    description:
      "Modules that connect the platform to engineering tools like Dassault 3DExperience, MSC NASTRAN, and PTC Creo.",
  },
  utilities: {
    icon: Wrench,
    description: "Command-line and utility tools such as the Istari CLI.",
  },
};

export function DashboardView() {
  const assetsQuery = useAssets();
  const assets = assetsQuery.data?.assets ?? [];
  const assetCounts = countUniqueAssetsByTopLevel(assets);

  if (assetsQuery.isLoading) {
    return (
      <div className="grid gap-6 sm:grid-cols-2">
        {ASSET_CATEGORIES.map((category) => (
          <Skeleton key={category.key} className="h-44 rounded-xl" />
        ))}
      </div>
    );
  }

  if (assetsQuery.isError && !assetsQuery.data) {
    return (
      <div role="alert" className="py-8 text-center text-muted-foreground">
        Unable to load assets
      </div>
    );
  }

  if (assets.length === 0) {
    return <div className="py-8 text-center text-muted-foreground">No assets available</div>;
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      {ASSET_CATEGORIES.map((category) => {
        const label = getGroupLabel(category.key);
        const count = category.count(assetCounts);
        const { icon: Icon, description } = CATEGORY_DETAILS[category.key];
        const card = (
          <Card className={cn("h-full transition-shadow", count === 0 ? "opacity-60" : "hover:shadow-md")}>
            <CardHeader>
              <div className="mb-2 flex items-center justify-between">
                <div className="flex size-12 items-center justify-center rounded-lg bg-brand-cloud dark:bg-primary/30">
                  <Icon className="size-6 text-brand-flight dark:text-brand-digital" />
                </div>
                <Badge variant="outline">{`${count} ${count === 1 ? "asset" : "assets"}`}</Badge>
              </div>
              <CardTitle className="text-lg">{label}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </CardHeader>
          </Card>
        );
        if (count === 0) {
          return <div key={category.key}>{card}</div>;
        }
        return (
          <Link
            key={category.key}
            to={assetCategoryPath(category)}
            className="rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {card}
          </Link>
        );
      })}
    </div>
  );
}
